import type {
  LogAccess,
  OrganizationSettings as Settings,
  PortalMe,
  PortalOrganization,
} from '@central/api';
import { Card, InfoRow, color, formatDate, logRetention, shownError, useReauth } from '@central/ui';
import { Alert, Button, Group, Loader, Modal, Radio, Stack, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api, meQuery, organizationQuery, settingsQuery } from '../api';
import { errorMessage } from '../format';

const logAccessLabel: Record<LogAccess, string> = {
  none: 'Not shared',
  owners: 'Owners',
  all: 'Everyone on the team',
};

type Editing = 'requireMfa' | 'crashAutoSend' | 'logAccess';

/** "Entra ID · for @riverside-dot.example", one provider per entry. */
function ssoLine(o: PortalOrganization): string {
  if (o.sso.length === 0) return 'Not set up';
  return o.sso
    .map((p) =>
      p.domains.length ? `${p.name} · for ${p.domains.map((d) => `@${d}`).join(', ')}` : p.name,
    )
    .join('; ');
}

/**
 * Organization on Team & security (docs/design/CustomerTeam.dc.html): a
 * full-width card, its rows in a grid. The account manager, maintenance
 * term and single sign-on come from Rethink's records and are read-only;
 * the settings below them are the organization's. Every role sees them; an Owner
 * changes one at a time through "Change", which confirms before saving
 * (crash reports are the customer's consent to send data). Turning off the
 * second-factor requirement needs a second factor proved in the last 10
 * minutes, so an Owner with none is told to set one up instead.
 */
export function OrganizationSettings({ me }: { me: PortalMe }) {
  const qc = useQueryClient();
  const settings = useQuery(settingsQuery);
  const org = useQuery(organizationQuery);
  const { guard, prompt } = useReauth(me.user.secondFactors, {
    api: api.auth,
    describeError: errorMessage,
  });
  const isOwner = me.user.role === 'Owner';
  const [editing, setEditing] = useState<Editing>();
  const save = useMutation({
    mutationFn: (next: Settings) => guard(() => api.settings.update(next)),
    onSuccess: async (s) => {
      qc.setQueryData(settingsQuery.queryKey, s);
      setEditing(undefined);
      // PortalMe carries requireMfa for the page subtitle.
      await qc.invalidateQueries({ queryKey: meQuery.queryKey });
    },
  });
  const s = settings.data;
  const change = (label: string, e: Editing) =>
    isOwner && (
      <>
        {' · '}
        <Button
          variant="subtle"
          size="compact-sm"
          px={4}
          aria-label={`Change ${label}`}
          onClick={() => {
            save.reset();
            setEditing(e);
          }}
        >
          Change
        </Button>
      </>
    );

  return (
    <Card title="Organization">
      {settings.error || org.error ? (
        <Alert color="red">{errorMessage(settings.error ?? org.error)}</Alert>
      ) : !s || !org.data ? (
        <Loader size="sm" aria-label="Loading" />
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              columnGap: 32,
            }}
          >
            <InfoRow label="Account manager">
              {org.data.accountManager ?? <span style={{ color: color.textMuted }}>None yet</span>}
            </InfoRow>
            <InfoRow label="Maintenance through">
              {org.data.maintenance.endsAt ? (
                formatDate(org.data.maintenance.endsAt)
              ) : (
                <span style={{ color: color.textMuted }}>No maintenance plan</span>
              )}
            </InfoRow>
            <InfoRow label="Single sign-on">
              <span style={org.data.sso.length ? undefined : { color: color.textMuted }}>
                {ssoLine(org.data)}
              </span>
            </InfoRow>
            <InfoRow label="Second factor">
              <span>{s.requireMfa ? 'Required for everyone' : 'Each person’s choice'}</span>
              {change('second factor', 'requireMfa')}
            </InfoRow>
            <InfoRow label="Automatic crash reports">
              <span>{s.crashAutoSend ? 'On' : 'Off'}</span>
              {change('automatic crash reports', 'crashAutoSend')}
            </InfoRow>
            <InfoRow label="Log access">
              <span>{logAccessLabel[s.logAccess]}</span>
              {change('log access', 'logAccess')}
            </InfoRow>
            <InfoRow label="Log retention">
              <span>{logRetention}</span>
            </InfoRow>
          </div>
          <p style={{ margin: '12px 0 0', fontSize: 13, color: color.textMuted }}>
            {isOwner
              ? 'A second-factor requirement applies at each person’s next sign-in.'
              : 'Only Owners change these.'}
          </p>
          {editing && (
            <ChangeDialog
              editing={editing}
              settings={s}
              me={me}
              busy={save.isPending}
              error={shownError(save.error)}
              onSave={(next) => save.mutate(next)}
              onClose={() => setEditing(undefined)}
            />
          )}
        </>
      )}
      {prompt}
    </Card>
  );
}

/** Confirms one setting's change before it is saved. */
function ChangeDialog({
  editing,
  settings: s,
  me,
  busy,
  error,
  onSave,
  onClose,
}: {
  editing: Editing;
  settings: Settings;
  me: PortalMe;
  busy: boolean;
  error: unknown;
  onSave: (next: Settings) => void;
  onClose: () => void;
}) {
  const [logAccess, setLogAccess] = useState<LogAccess>(s.logAccess);
  const f = me.user.secondFactors;
  const hasFactor = f.totp || f.passkeys > 0;

  let title: string;
  let body: ReactNode;
  // next is the whole object to save; undefined offers no save button.
  let next: Settings | undefined;
  let confirm = '';
  if (editing === 'requireMfa') {
    if (s.requireMfa) {
      title = 'Stop requiring a second factor?';
      if (hasFactor) {
        body = 'Each person chooses whether to use one. You’ll confirm it’s you first.';
        next = { ...s, requireMfa: false };
        confirm = 'Stop requiring';
      } else {
        body =
          'Turning this off needs a second factor on your own account. Set up an authenticator app or a passkey under Your sign-in, then come back.';
      }
    } else {
      title = 'Require a second factor for everyone?';
      body =
        'Everyone will need a passkey or an authenticator app to sign in, from their next sign-in.';
      next = { ...s, requireMfa: true };
      confirm = 'Require it';
    }
  } else if (editing === 'crashAutoSend') {
    if (s.crashAutoSend) {
      title = 'Stop sending crash reports automatically?';
      body =
        'Crash bundles from your dedicated deployments will no longer be sent to Rethink. You are still told when a service restarts after a crash.';
      next = { ...s, crashAutoSend: false };
      confirm = 'Stop sending';
    } else {
      title = 'Send crash reports automatically?';
      body =
        'When a service on one of your dedicated deployments restarts after a crash, its crash bundle is sent to Rethink to diagnose it. Shared deployments never send one.';
      next = { ...s, crashAutoSend: true };
      confirm = 'Turn on';
    }
  } else {
    title = 'Who may search logs?';
    body = (
      <Radio.Group value={logAccess} onChange={(v) => setLogAccess(v as LogAccess)}>
        <Stack gap={8}>
          {(Object.keys(logAccessLabel) as LogAccess[]).map((v) => (
            <Radio key={v} value={v} label={logAccessLabel[v]} />
          ))}
        </Stack>
      </Radio.Group>
    );
    next = logAccess === s.logAccess ? undefined : { ...s, logAccess };
    confirm = 'Save';
  }

  return (
    <Modal opened onClose={onClose} title={title}>
      <Stack gap={14}>
        {typeof body === 'string' ? <Text size="sm">{body}</Text> : body}
        {error ? <Alert color="red">{errorMessage(error)}</Alert> : null}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {confirm ? 'Cancel' : 'Close'}
          </Button>
          {confirm && (
            <Button loading={busy} disabled={!next} onClick={() => next && onSave(next)}>
              {confirm}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
